/**
 * Date arithmetic for ./DateInput, kept free of `Date` parsing.
 *
 * The wire value is ISO `YYYY-MM-DD` and is built from integers, never through
 * `Date#toISOString`: that renders in UTC, so an applicant at UTC+7 picking a
 * date at local midnight would submit the day BEFORE the one they chose.
 */

export interface Ymd {
  year: number;
  /** 1-12. */
  month: number;
  /** 1-31. */
  day: number;
}

/**
 * The days a date field offers, and where it opens when empty. Both pickers —
 * the phone's wheels and the desktop calendar — read the same range, so they
 * cannot disagree about which dates exist.
 */
export interface DateRange {
  /** The earliest day that can be picked. */
  min: Ymd;
  /** The latest day that can be picked. */
  max: Ymd;
  /** Where an empty field's picker opens. */
  opensAt: Ymd;
}

/** The oldest birth year offered. */
export const MAX_AGE_YEARS = 120;

/** Where an empty date of birth opens: the year most applicants are near. */
export const STARTING_AGE = 30;

/**
 * How far an expiry date reaches: a passport runs ten years at most. As far
 * back as forward, because the date is read off the document and an expired
 * one is for a check to judge, not for the field to refuse.
 */
export const EXPIRY_YEARS = 10;

/** How far back a past date (a company's incorporation) reaches. */
export const PAST_YEARS = 150;

export function daysInMonth(year: number, month: number): number {
  // Day 0 of the NEXT month is the last day of this one; UTC so no DST shift.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function todayYmd(now: Date = new Date()): Ymd {
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

/** A date of birth: today at the latest, opening thirty years back. */
export function birthDateRange(today: Ymd = todayYmd()): DateRange {
  return {
    min: { year: today.year - MAX_AGE_YEARS, month: 1, day: 1 },
    max: today,
    opensAt: { year: today.year - STARTING_AGE, month: 1, day: 1 },
  };
}

/** A document's expiry date, opening on this month. */
export function expiryDateRange(today: Ymd = todayYmd()): DateRange {
  return {
    min: { year: today.year - EXPIRY_YEARS, month: 1, day: 1 },
    max: { year: today.year + EXPIRY_YEARS, month: 12, day: 31 },
    opensAt: today,
  };
}

/** A day that has happened, such as a company's incorporation; opens on this month. */
export function pastDateRange(today: Ymd = todayYmd()): DateRange {
  return { min: { year: today.year - PAST_YEARS, month: 1, day: 1 }, max: today, opensAt: today };
}

const within = (value: number, low: number, high: number) => Math.min(Math.max(value, low), high);

/**
 * Pull a draft back inside the real calendar and the range, field by field —
 * moving the year keeps the month and day where it can, as a wheel should.
 */
export function clampYmd(value: Ymd, { min, max }: Pick<DateRange, 'min' | 'max'>): Ymd {
  const year = within(value.year, min.year, max.year);
  const month = within(
    value.month,
    year === min.year ? min.month : 1,
    year === max.year ? max.month : 12,
  );
  const day = within(
    value.day,
    year === min.year && month === min.month ? min.day : 1,
    year === max.year && month === max.month ? max.day : daysInMonth(year, month),
  );
  return { year, month, day };
}

export function toIso({ year, month, day }: Ymd): string {
  const pad = (n: number, width: number) => String(n).padStart(width, '0');
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

/** `null` for '' or anything that is not a real calendar date. */
export function parseIso(value: string): Ymd | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

/**
 * Which calendar the applicant reads years in — the field, the year wheel and
 * the calendar all go through this, so the year picked is the year shown on
 * the field. The stored value is always Gregorian ISO regardless.
 */
export function calendarFor(language: string): 'buddhist' | 'gregory' {
  return language.startsWith('th') ? 'buddhist' : 'gregory';
}

/** A year as the applicant counts it — the Buddhist era in Thai. */
export function yearFormat(language: string): (year: number) => string {
  const fmt = new Intl.DateTimeFormat(language, {
    year: 'numeric',
    timeZone: 'UTC',
    calendar: calendarFor(language),
  });
  // The bare number only — Thai Buddhist formatting prefixes the era (พ.ศ.).
  return (year) =>
    fmt.formatToParts(Date.UTC(year, 6, 1)).find((p) => p.type === 'year')?.value ?? String(year);
}
